import * as React from "react";
import {authorize, Overlay} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import * as moment from "moment";
import {
    CommonHelpers,
    Document,
    FalconNavigatorHeader,
    ICustomerPortalService,
    Policy,
    productHelper,
    ProductsEnum,
    Renewal,
    Types,
    CAR_CERTIFICATE_FILENAME,
    CAR_MTA_CONFIRMATION_PACK,
    CAR_WELCOME_PACK,
    CAR_WOLF_CANCELLATION_EMAIL_TITLE,
    CAR_WOLF_CHARGEBACK_CANCELLATION,
    CAR_WOLF_CHARGEBACK_COVER,
    CAR_WOLF_CHARGEBACK_NO_COVER,
    CAR_WOLF_RENEWAL_ACCEPTANCE,
    CAR_WOLF_RENEWAL_CLAIMS_IMPACT,
    CAR_WOLF_RENEWAL_INVITE_PACK,
    CAR_WOLF_RENEWAL_NCD_PROOF,
    FREETEXT_DOCUMENT,
    NON_PRINTABLE_DOCUMENTS,
    POLICY_STATUS_PENDING_RENEWAL
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {finalize, map} from "rxjs/operators";
import {RouteComponentProps} from "react-router-dom";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {downloadDocument, trimFileExtension} from "../../../../../CommonFunctions";
import {FalconAccordion} from "../../../../../components/FalconAccordion";
import {mapCommunicationTitles, mapDocumentTitles} from "../../../../../utils/DataMappers";
import {RenewalDocumentsBanner} from "../../../../../components/banners/RenewalDocumentsBanner";
import {PostRequestedDocumentsButton} from "./PostRequestedDocumentsButton";
import {DocumentListModal} from "@falcon/plugin-falcon-journey";

interface IDocumentsPageParams {
    policyNumber: string;
}

interface IDocumentsPageProps extends RouteComponentProps<IDocumentsPageParams> {
    location: any;
}

interface IState {
    policies?: Policy[];
    carsDocuments: Document[];
    archiveDocuments: Document[];
    archivedDocumentsElementsByDate: JSX.Element[];
    carsRenewalDocuments: Document[];
    communications: Document[];
    direction: string;
    active: boolean;
    isWhite: boolean;
    day: number;
    isDocumentDownloaded: boolean;
    receiveByPostChecked: boolean | null;
    renewalProposal?: Renewal;
    isRenewed?: boolean;
    showLoading: boolean;
    lastYearsPolicy?: Policy;
}

@authorize
export class AnnualDocumentsPage extends React.Component<IDocumentsPageProps, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private readonly FILE_PATH: string = "path";
    private readonly FILE_NAME: string = "fileName";
    private readonly FILE_ID: string = "id";
    private readonly FILE_LABEL: string = "label";

    constructor(props: Readonly<IDocumentsPageProps>) {
        super(props);
        this.state = {
            direction: 'asc',
            carsDocuments: [],
            carsRenewalDocuments: [],
            archiveDocuments: [],
            archivedDocumentsElementsByDate: [],
            communications: [],
            active: false,
            isWhite: true,
            day: 0,
            isDocumentDownloaded: true,
            receiveByPostChecked: null,
            renewalProposal: undefined,
            showLoading: true,
            lastYearsPolicy: undefined
        };

        this.sortByDate = this.sortByDate.bind(this);
        this.renderDocumentsByPost = this.renderDocumentsByPost.bind(this);
        this.showMore = this.showMore.bind(this);
        this.createPDFMap = this.createPDFMap.bind(this);

    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Policy documents");

        this.customerPortalService
            .getPolicies()
            .pipe(map((policies: Policy[]) => policies.filter((policy: Policy) => !policy.endDate || moment(policy.endDate).isSameOrAfter(moment().startOf("day")))))
            .subscribe((activePolicies: Policy[]) => {
                this.customerPortalService
                    .getExpiredPolicies(ProductsEnum.WOLF_MOTOR)
                    .subscribe(expiredPolicies => {
                        const filteredActivePolicies: Policy[] = activePolicies
                            .filter((policy: Policy) => policy.policyNumber === this.props.match.params.policyNumber)
                            .sort((a, b) => moment(b.endDate).diff(a.endDate));

                        const policies = [...(filteredActivePolicies || []), ...(expiredPolicies || [])];

                        this.setState({
                            policies
                        });
                        this.customerPortalService.getAllDocuments().subscribe(
                            (documents: Document[]) => {
                                this.customerPortalService.getRenewalProposal(this.props.match.params.policyNumber).subscribe(
                                    (renewalProposal: Renewal) => {
                                        this.setState({renewalProposal});
                                        this.fetchDocuments(policies, renewalProposal, documents);
                                    });
                                this.fetchCommunications(policies, documents);
                            });

                        this.customerPortalService.getPolicyHistoryAfterRenewal()
                            .pipe(finalize(() => {
                                this.setState({ showLoading: false });
                            }))
                            .subscribe((policyHistories: Policy[]) => {
                                const lastYearsPolicy = this.getLastPolicy(policyHistories);
                                this.setState({
                                    isRenewed: !!lastYearsPolicy,
                                    ...(lastYearsPolicy ? { lastYearsPolicy } : {})
                                });
                            });
                    });
            });
    }

    public render() {
        const hasPendingRenewal: boolean = this.state.renewalProposal != null && this.state.renewalProposal.status === POLICY_STATUS_PENDING_RENEWAL;

        return (
            <React.Fragment>
                <FalconHeader/>

                <div className="plugin">
                    <FalconNavigatorHeader
                        back={this.state.policies && `/dashboard/annualMotor/${this.state.policies[0].policyNumber}`}
                        title="Your car policy documents"
                    />
                    {!this.state.showLoading && <div className="documents-page">
                        {hasPendingRenewal && <RenewalDocumentsBanner/>}
                        {hasPendingRenewal && this.renderRenewalDocumentsPanel()}
                        {hasPendingRenewal && this.renderExistingDocumentsPanel(false)}
                        {this.state.isRenewed && this.renderExistingDocumentsPanel(true)}
                        {!hasPendingRenewal && !this.state.isRenewed && this.renderDocumentsPanel()}

                        {this.renderDocumentsArchivePanel()}
                        {this.renderDocumentsByPost()}
                    </div>}
                    {this.state.policies && this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualMotor/${this.state.policies[0].policyNumber}`)}
                </div>
                <Overlay show={this.state.showLoading}/>
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private renderDocumentsArchivePanel(): JSX.Element {
        if (this.state.archiveDocuments && this.state.archiveDocuments.length !== 0) {
            let agreementVersionIndex: any;
            this.state.archiveDocuments.find(doc => {
                doc.tags.find((tag, index) => {
                    if (tag.replace(/[0-9]/g, '') === "agreement_version_") {
                        return agreementVersionIndex = index;
                    } else {
                        return undefined;
                    }
                })
            });
            if (agreementVersionIndex !== undefined) {
                this.state.archiveDocuments.sort((a, b) => b.tags[agreementVersionIndex].localeCompare(a.tags[agreementVersionIndex]) || moment(b.date).diff(a.date));
            } else {
                this.state.archiveDocuments.sort((a, b) => moment(b.date).diff(a.date));
            }
            if (!this.state.isRenewed) {
                return (
                    <div
                        className="widget documents-widget panel documents-archive">
                        <p className={"documents-archive-title font-weight-bold text-center"}>Documents archive</p>
                        <table>
                            <tbody>
                            {this.renderTableHeader()}
                            {this.state.archivedDocumentsElementsByDate}
                            </tbody>
                        </table>
                        {this.renderLoadMoreButton()}
                    </div>
                );
            } else {
                return (
                    <div className="widget documents-widget documents-archived-widget panel">
                        <ul className="list-unstyled">
                            <FalconAccordion
                                togglePositionTop={true}
                                toggleShown={<div className="documents-archived-title toggle d-flex justify-content-between align-items-center">
                                    <span/>
                                    <span
                                        className="font-weight-bold text-center fs-18">Archived policy documents</span>
                                    <span className="toggle-icon collapse-icon"/></div>}
                                toggleHidden={<div className="documents-archived-title toggle d-flex justify-content-between align-items-center">
                                    <span/>
                                    <span
                                        className="font-weight-bold text-center fs-18">Archived policy documents</span>
                                    <span className="toggle-icon expand-icon"/></div>}>
                                {this.renderDocuments(this.state.archiveDocuments)}
                                <DocumentListModal
                                    productName={productHelper.parseProductName(this.state.lastYearsPolicy?.products[0] as string)}
                                    policyDate={new Date(this.state.lastYearsPolicy?.interaction.factors.originalQuoteDate)}
                                    isBDIVisible={this.state.lastYearsPolicy?.interaction.optionalExtras.breakdownCover}
                                    isMLEVisible={this.state.lastYearsPolicy?.interaction.optionalExtras.motorLegalExpenses}
                                    isAccordion={true}
                                    coverTier={this.state.lastYearsPolicy?.interaction.coverTier}
                                />
                            </FalconAccordion>
                        </ul>
                    </div>
                );
            }
        } else {
            return <></>;
        }
    }

    private getLastPolicy(policyHistories: Policy[]): Policy | null {
        if (!policyHistories?.length) { return null; }

        return policyHistories.reduce(
            (latestSoFar, current) =>
                new Date(current.endDate) > new Date(latestSoFar.endDate)
                    ? current
                    : latestSoFar,
            policyHistories[0]
        );
    }

    private renderDocumentsByPost() {
        return (
            <div className="receive-by-post">
                <p className="receive-by-post-title font-weight-bold fs-18">Receive my policy documents by post</p>
                <p className="receive-by-post-description">We've gone paperless; we care about the environment and it's better for everyone! But if you wish,
                    you can request below to have your documents posted to you to the address listed on your policy</p>
                {(this.state.carsDocuments || this.state.carsRenewalDocuments) && this.state.policies &&
                    <PostRequestedDocumentsButton policy={this.state.policies[0]}
                                                  allDocuments={CommonHelpers.getPrintableDocuments(this.state.carsDocuments.concat(this.state.carsRenewalDocuments), NON_PRINTABLE_DOCUMENTS)}/>}
            </div>
        )
    }

    private renderDocumentsPanel(): JSX.Element {
        if (this.state.policies){
            return (
                <div className="widget documents-widget active-policy-documents-widget panel">
                    <img className="mr-2 wolf-documents-page-icon mb-3" alt="document-icon" src="/img/wolf-your-car.png"/>
                    <p className="active-policy-documents-title font-weight-bold text-center fs-18">Policy documents</p>
                    <ul className="list-unstyled">
                        {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                        {this.renderDocuments(this.state.carsDocuments)}
                        <FalconAccordion toggleHidden={<div className="toggle">+ Show more</div>}
                                         toggleShown={<div className="toggle">- Show less</div>}>
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policies[0]?.products[0])}
                                policyDate={new Date(this.state.policies[0].interaction.factors.originalQuoteDate)}
                                isBDIVisible={this.state.policies[0].interaction.optionalExtras.breakdownCover}
                                isMLEVisible={this.state.policies[0].interaction.optionalExtras.motorLegalExpenses}
                                isAccordion={false}
                                coverTier={this.state.policies[0].interaction.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
        } else {
            return <></>;
        }
    }

    private renderExistingDocumentsPanel(isShown: boolean): JSX.Element {
       const cssClass =  this.state.isRenewed ? "existing-documents-widget-pending-renewal" : "existing-documents-widget";
       if(this.state.policies){
           return (
                <div className={`widget documents-widget panel ${cssClass}`}>
                    <ul className="list-unstyled">
                        <FalconAccordion
                            togglePositionTop={true}
                            isShown={isShown}
                            toggleShown={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-collapsed"><span/>
                                <span className="font-weight-bold text-center fs-18">Existing policy documents</span>
                                <span className="toggle-icon collapse-icon"/></div>}
                            toggleHidden={<div className="existing-documents-title toggle d-flex justify-content-between align-items-center accordion-expanded"><span/>
                                <span className="font-weight-bold text-center fs-18">Existing policy documents</span>
                                <span className="toggle-icon expand-icon"/></div>}>
                            {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                            {this.renderDocuments(this.state.carsDocuments)}
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policies[0]?.products[0])}
                                policyDate={new Date(this.state.policies[0].interaction.factors.originalQuoteDate)}
                                isBDIVisible={this.state.policies[0].interaction.optionalExtras.breakdownCover}
                                isMLEVisible={this.state.policies[0].interaction.optionalExtras.motorLegalExpenses}
                                isAccordion={false}
                                coverTier={this.state.policies[0].interaction.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
       } else {
           return <></>;
       }
    }

    private renderRenewalDocumentsPanel(): JSX.Element {
        if (this.state.policies && this.state.renewalProposal){
            return (
                <div className="widget documents-widget renewal-documents-widget panel">
                    <ul className="list-unstyled">
                        <FalconAccordion isShown={true} togglePositionTop={true} toggleHidden={<div
                            className="renewal-documents-title toggle d-flex justify-content-between align-items-center accordion-collapsed"><span/><span
                            className="font-weight-bold text-center fs-18">Renewal policy documents</span><span
                            className="toggle-icon expand-icon"/></div>}
                                         toggleShown={<div
                                             className="renewal-documents-title toggle d-flex justify-content-between align-items-center accordion-expanded">
                                             <span/><span
                                             className="font-weight-bold text-center fs-18">Renewal policy documents</span><span
                                             className="toggle-icon collapse-icon"/></div>}>
                            {this.state.communications.length > 0 && this.renderDocuments(this.state.communications)}
                            {this.renderDocuments(this.state.carsRenewalDocuments)}
                            <DocumentListModal
                                productName={productHelper.parseProductName(this.state.policies[0]?.products[0])}
                                policyDate={new Date(this.state.renewalProposal.renewalDetails.periodStartDate)}
                                isBDIVisible={this.state.renewalProposal.optionalExtras.breakdownCover}
                                isMLEVisible={this.state.renewalProposal.optionalExtras.motorLegalExpenses}
                                isAccordion={true}
                                coverTier={this.state.renewalProposal.coverTier}
                            />
                        </FalconAccordion>
                    </ul>
                </div>
            );
        } else {
            return <></>;
        }
    }

    private renderDocuments(documents: Document[]): JSX.Element[] {
        return documents.map((document: Document, index: number) => (
            <li className="media" key={index}>
                <div className="media-body">
                    <div className="row height-0">
                        <div className="col-1"/>
                        <div className="col-11">
                            <div className="dashed-10 ml-3 mr-3"/>
                        </div>
                    </div>
                    <a className="document-download-link" onClick={this.downloadDocument(document)}
                       id={trimFileExtension(document.name)}>
                        <img className="pdf-icon active-icon" alt="Document download icon"
                             src="/img/pdf-icon.png"/>
                        <h5 className="pt-2 document-name">{document.title}</h5>
                    </a>
                </div>
            </li>
        ));
    }

    private renderTableHeader(): JSX.Element {
        return (
            <React.Fragment>
                <tr>
                    <th className={"pb-3 pl-3"}>
                        <span className="documents-archive-table-header muli-semi-bold-16">Date</span>
                        <span onClick={this.sortByDate} className={`${this.state.active &&
                        (this.state.direction === "asc" ? "fas fa-sort-up" : "fas fa-sort-down")}
                            ${!this.state.active && "fas fa-sort"} table-sort-arrow`}/>
                    </th>
                </tr>
            </React.Fragment>
        )
    }

    private renderLoadMoreButton(): JSX.Element {
        if (!this.state.day) {
            return <></>;
        }
        if (this.state.day >= 4) {
            return (
                <button type="button" className="btn btn-primary btn-next plugin"
                        onMouseDown={this.showMore}
                        onTouchStart={this.showMore}
                        id={"show-more"}>
                    Load more
                </button>
            )
        }

        return <></>;
    }

    private fetchDocuments = (policies?: Policy[], renewalProposal?: Renewal, documents?: Document[]) => {
        if (policies && policies.length > 0 && documents) {
            const carsRenewalDocuments: Document[] = [];
            const carsDocuments: Document[] = [];
            const archiveDocuments: Document[] = [];
            const ncdProofDocs: Document[] = [];
            let hasActivePolicies: boolean = false;
            policies.forEach((policy: Policy) => {
                if (renewalProposal) {
                    const renewalDocs = documents.filter(doc => {
                        return doc.tags.filter(d => d === ("renewal_" + renewalProposal._id)).length > 0;
                    });

                    const renewalPdfs = renewalDocs.filter(doc =>
                        doc.name.includes(CAR_WOLF_RENEWAL_CLAIMS_IMPACT)
                        || doc.name.includes(CAR_WOLF_RENEWAL_NCD_PROOF));

                    const invitePdfs = renewalDocs.filter(doc =>
                        doc.name.includes(CAR_WOLF_RENEWAL_INVITE_PACK));

                    // sort by date from latest and get latest generated renewal invite to avoid duplicates in documents view
                    if (invitePdfs.length > 0) {
                        let latestInvitePdf = invitePdfs.sort((a, b) => moment(b.date).diff(moment(a.date)))[0];
                        renewalPdfs.push(latestInvitePdf);
                    }
                    carsRenewalDocuments.push(...mapDocumentTitles(renewalPdfs.reverse(), policy));
                }

                const allPolicyDocs = documents.filter(doc => {
                    return doc.tags.filter(d => d === ("agreement_" + policy.policyNumber)).length > 0
                });

                const currentPolicyVersionDocs = allPolicyDocs.filter(doc => {
                    return doc.tags.filter(d => d === ("agreement_version_" + policy.interaction.agreementVersion)).length > 0
                });

                const documentsToBeListed = currentPolicyVersionDocs.filter(doc =>
                    doc.name.includes(CAR_CERTIFICATE_FILENAME)
                    || doc.name.includes(CAR_WOLF_RENEWAL_ACCEPTANCE)
                    || doc.name.includes(CAR_WELCOME_PACK)
                    || doc.name.includes(CAR_MTA_CONFIRMATION_PACK));

                if (!hasActivePolicies && !productHelper.isActivePolicy(policy)) {
                    const policyNcdProofDoc = allPolicyDocs.filter(doc => doc.name.includes(CAR_WOLF_RENEWAL_NCD_PROOF + ".pdf"));

                    if (policyNcdProofDoc) {
                        ncdProofDocs.push(...mapDocumentTitles(policyNcdProofDoc, policy));
                    }
                } else {
                    hasActivePolicies = true;
                }

                const mappedCurrentDocuments = mapDocumentTitles(documentsToBeListed, policy);
                if (productHelper.includesWolfMotorProduct([policy])) {
                    carsDocuments.push(...mappedCurrentDocuments);
                }

                const documentsToBeListedWithoutAgreementVersion = allPolicyDocs.filter(doc =>
                    (doc.name.includes(CAR_CERTIFICATE_FILENAME) && !doc.tags.includes(("agreement_version_" + policy.interaction.agreementVersion)))
                    || doc.name.includes(CAR_WELCOME_PACK)
                    || doc.name.includes(CAR_MTA_CONFIRMATION_PACK)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_COVER)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_NO_COVER)
                    || doc.name.includes(CAR_WOLF_CHARGEBACK_CANCELLATION)
                    || doc.name.includes(FREETEXT_DOCUMENT)
                    || doc.name.includes(CAR_WOLF_RENEWAL_INVITE_PACK)
                );

                if (productHelper.includesWolfMotorProduct([policy])) {
                    archiveDocuments.push(...mapDocumentTitles(documentsToBeListedWithoutAgreementVersion.reverse(), policy));
                }
            });

            if (!hasActivePolicies && ncdProofDocs && ncdProofDocs.length > 0) {
                let latestNcdProofDoc = ncdProofDocs.sort((a, b) => moment(b.date).diff(moment(a.date)))[0];
                carsDocuments.push(latestNcdProofDoc);
            }

            const archivedDocumentsElementsByDate = this.getArchivedDocumentsByDate(archiveDocuments, false);

            this.setState({
                carsRenewalDocuments,
                carsDocuments,
                archiveDocuments,
                archivedDocumentsElementsByDate
            })
        }
    };

    private fetchCommunications = (policies?: Policy[], documents?: Document[]) => {
        if (policies && documents) {
            const communications: Document[] = [];
            const mtcPolicy = policies.find((policy: Policy) => policy.status.match(/mtc/i) && productHelper.isWolfMotor(policy.interaction));
            if (mtcPolicy) {
                const mtcDocs = documents.filter(doc => doc.name.includes(CAR_WOLF_CANCELLATION_EMAIL_TITLE));
                communications.push(...mapCommunicationTitles(mtcDocs, mtcPolicy));
            }
            this.setState({communications});
        }
    }

    private downloadDocument = (doc: Document) => (event: any): void => {
        this.setState({isDocumentDownloaded: false});
        this.customerPortalService.downloadDocument(doc.id).subscribe(
            (documentContent: ArrayBuffer) => {
                if (documentContent) {
                    downloadDocument(documentContent, doc.downloadName);
                }
                this.setState({isDocumentDownloaded: true});
            }
        );
    };

    private sortByDate(): void {
        const sortedDates = this.state.direction === 'asc' ?
            this.state.archiveDocuments!.sort((a, b) => moment(a.date).diff(b.date))
            :
            this.state.archiveDocuments!.sort((a, b) => moment(b.date).diff(a.date));
        this.setState({
            archiveDocuments: sortedDates,
            direction: this.state.direction === 'asc' ? 'desc' : 'asc',
            active: true
        });
    }

    private showMore(): void {
        this.setState({
            archivedDocumentsElementsByDate: this.getArchivedDocumentsByDate(this.state.archiveDocuments, true)
        })
    }

    private createPDFMap(path: string, fileName: string, id: string, label: string): Map<string, string> {
        return new Map()
            .set(this.FILE_PATH, path)
            .set(this.FILE_NAME, fileName)
            .set(this.FILE_ID, id)
            .set(this.FILE_LABEL, label);
    }

    private getArchivedDocumentsByDate(archivedDocuments: Document[], showHistory: boolean): JSX.Element[] {
        let documents: JSX.Element[];
        let day: number = 0;
        if (archivedDocuments && archivedDocuments.length !== 0) {
            let previousDate: string | null = "";
            let isWhite: boolean = true;
            documents = archivedDocuments.map((document, index) => {
                const {date, title, name} = document;
                if (PluginTemplates.convertDate(date, "DD MMMM YYYY") === previousDate) {
                    const backGround: string = isWhite ? "" : "light-blue-background";
                    const history: string = day >= 4 ? "display-none" : "";
                    return (
                        <tr key={index}
                            className={`${backGround} ${history} ${showHistory ? "display-block" : ""}`}>
                            <td id="document-date" className="muli-semi-bold-16">
                                <div className={"row ml-2"}>
                                    <h5 className={"col-10 pt-3 pl-0"}
                                        onClick={this.downloadDocument(document)}>{title}</h5>
                                    <a className="col-2 pl-1 document-download-link document-download-link-documents-page"
                                       onClick={this.downloadDocument(document)} id={trimFileExtension(name)}>
                                        <img className="active-policy-download-icon" alt="Document download icon"
                                             src="/img/Flow-download.svg"/>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    )
                } else {
                    previousDate = PluginTemplates.convertDate(date, "DD MMMM YYYY");
                    isWhite = !isWhite;
                    day += 1;
                    const backGround: string = isWhite ? "" : "light-blue-background";
                    const history: string = day >= 4 ? "display-none" : "";
                    return (
                        <tr key={index}
                            className={`${backGround} ${history} ${showHistory ? "display-block" : ""}`}>
                            <td id="document-date" className="muli-semi-bold-16 pt-3">
                                <span
                                    className="font-weight-extra-light fs-14 ml-2">{PluginTemplates.convertDate(date, "DD MMMM YYYY")}</span>
                                <div className={"row ml-2"}>
                                    <h5 className={"col-10 pt-3 pl-0"}
                                        onClick={this.downloadDocument(document)}>{title}</h5>
                                    <a className="col-2 pl-1 document-download-link document-download-link-documents-page"
                                       onClick={this.downloadDocument(document)} id={trimFileExtension(name)}>
                                        <img className="active-policy-download-icon" alt="Document download icon"
                                             src="/img/Flow-download.svg"/>
                                    </a>
                                </div>
                            </td>
                        </tr>
                    )
                }

            });
        } else {
            return [];
        }
        this.setState({day});
        return documents;
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/annual/motor/documents/AnnualDocumentsPage.tsx